import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

/**
 * Auth lives in an httpOnly cookie set by the backend on signup/login/google-session.
 * The frontend never reads or stores tokens — `withCredentials: true` in axios
 * means the browser includes the cookie on every API call automatically.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      if (err?.response?.status && err.response.status >= 500) {
        // eslint-disable-next-line no-console
        console.warn("[auth] /auth/me failed:", err.response.status);
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // Skip /me during OAuth callback — AuthCallback handler will exchange the session and call refresh()
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const loginEmail = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const signupEmail = useCallback(async (email, password, name, role = "customer") => {
    const { data } = await api.post("/auth/signup", { email, password, name, role });
    setUser(data.user);
    return data.user;
  }, []);

  const loginGoogle = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[auth] logout API call failed; clearing client session anyway", err);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginEmail, signupEmail, loginGoogle, logout, refresh, setUser }),
    [user, loading, loginEmail, signupEmail, loginGoogle, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
